import {DocumentType, IdentityDocumentType, PartyType} from '@sollapay/enums';
import {buildIdentityKey, getCountryDisplayName, normalizeIdentityNumber} from '@sollapay/utils';

/** The fields that decide whether two individuals are the same person. */
export interface IndividualIdentity {
  idNumber?: string | null;
  idType?: IdentityDocumentType | null;
  idCountry?: string | null;
}

export function isPassportIdentity(idType: IdentityDocumentType | null | undefined): boolean {
  return idType === IdentityDocumentType.PASSPORT;
}

/** Same key the seller roster dedups on — only a passport is scoped by issuing country. */
export function resolveIndividualIdentityKey(identity: IndividualIdentity): string | null {
  return buildIdentityKey({
    idNumber: identity.idNumber,
    isPassport: isPassportIdentity(identity.idType),
    idCountry: identity.idCountry
  });
}

/** A passport number is ambiguous without its issuing country, so the country follows it. */
export function formatIndividualIdentity(
  identity: IndividualIdentity,
  locale: string
): string | null {
  if (!identity.idNumber) return null;
  return isPassportIdentity(identity.idType) && identity.idCountry
    ? `${identity.idNumber} (${getCountryDisplayName(identity.idCountry, locale)})`
    : identity.idNumber;
}

/** Stores a passport number in the same canonical form the identity key compares, so the DB constraint agrees. */
export function normalizeIdNumber(idType: IdentityDocumentType, idNumber: string): string {
  return isPassportIdentity(idType) ? normalizeIdentityNumber(idNumber) : idNumber.trim();
}

/** Drops a country left over from a passport selection so it can't split a national-ID match. */
export function normalizeIdCountry(
  idType: IdentityDocumentType,
  idCountry: string | null | undefined
): string | null {
  return isPassportIdentity(idType) ? idCountry?.trim().toUpperCase() || null : null;
}

export function getIdentityDocumentType(idType: IdentityDocumentType): DocumentType {
  return isPassportIdentity(idType)
    ? DocumentType.PASSPORT_DOCUMENT
    : DocumentType.NATIONAL_ID_DOCUMENT;
}

export const IDENTITY_DOCUMENT_TYPES: readonly DocumentType[] =
  Object.values(IdentityDocumentType).map(getIdentityDocumentType);

export function isIdentityDocumentType(documentType: DocumentType): boolean {
  return IDENTITY_DOCUMENT_TYPES.includes(documentType);
}

export const COMPANY_DOCUMENT_TYPES: readonly DocumentType[] = [
  DocumentType.COMPANY_REGISTRATION_EXTRACT,
  DocumentType.INCORPORATION_CERTIFICATE,
  DocumentType.AUTHORIZED_SIGNATORIES_PROTOCOL
];

export function isCompanyDocumentType(documentType: DocumentType): boolean {
  return COMPANY_DOCUMENT_TYPES.includes(documentType);
}

/** A company holds company documents; a person holds the one scan matching how they identify (national ID when unset). */
export function getAcceptedKycDocumentTypes(params: {
  partyType: PartyType;
  idType?: IdentityDocumentType | null;
}): DocumentType[] {
  return params.partyType === PartyType.ORGANIZATION
    ? [...COMPANY_DOCUMENT_TYPES]
    : [getIdentityDocumentType(params.idType ?? IdentityDocumentType.NATIONAL_ID)];
}
